import React, { useState } from "react";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { brand } from "../mock";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", goal: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing details", description: "Name and email are required." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setDone(true);
      toast({ title: "Request received", description: "Arnab’s team will respond within 24 hrs." });
      const saved = JSON.parse(localStorage.getItem("gt_leads") || "[]");
      localStorage.setItem("gt_leads", JSON.stringify([...saved, { ...form, ts: Date.now() }]));
      setForm({ name: "", email: "", goal: "", message: "" });
    }, 900);
  };

  return (
    <section id="contact" className="py-28 relative overflow-hidden">
      <div className="absolute inset-0 gt-radial-glow opacity-70 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.32em] text-emerald-400 uppercase mb-4">07 — Contact</div>
            <h2 className="gt-serif text-4xl md:text-6xl leading-[1.02]">
              Book the room. <br />
              <span className="text-neutral-500">Book the revenue.</span>
            </h2>
            <p className="mt-6 text-neutral-400 max-w-md">
              Discovery calls are complimentary and deliberately short. Come with a real deal,
              a real audience, or a real language block. Leave with a plan.
            </p>

            <div className="mt-10 space-y-4">
              <ContactRow icon={Phone} label="Phone" value={brand.phone} href={`tel:${brand.phone.replace(/\s/g, "")}`} />
              <ContactRow icon={Mail} label="Email" value={brand.email} href={`mailto:${brand.email}`} />
              <ContactRow icon={MapPin} label="Base" value={brand.location} />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="gt-hairline rounded-3xl p-8 md:p-10 bg-white/[0.02] backdrop-blur">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Full name" />
                <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="you@company.com" />
              </div>
              <div className="mt-5">
                <label className="text-xs tracking-[0.3em] text-neutral-500 uppercase">Goal</label>
                <select
                  value={form.goal}
                  onChange={(e) => setForm({ ...form, goal: e.target.value })}
                  className="mt-2 w-full bg-transparent gt-hairline rounded-xl px-4 py-3 text-white focus:border-emerald-400 outline-none"
                >
                  <option value="" className="bg-[#0a0a0b]">Select a track</option>
                  <option className="bg-[#0a0a0b]">UDEVA — English & Voice</option>
                  <option className="bg-[#0a0a0b]">Science of Sales — Closing Cohort</option>
                  <option className="bg-[#0a0a0b]">Pitch Ready — 1:1 Sprint</option>
                  <option className="bg-[#0a0a0b]">Corporate / Institutional Tie-up</option>
                </select>
              </div>
              <div className="mt-5">
                <label className="text-xs tracking-[0.3em] text-neutral-500 uppercase">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={5}
                  placeholder="Tell us about the deal, the audience, or the block."
                  className="mt-2 w-full bg-transparent gt-hairline rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:border-emerald-400 outline-none resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="mt-8 gt-btn-primary px-6 py-3.5 rounded-full inline-flex items-center gap-2 disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending
                  </>
                ) : done ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Sent — send another
                  </>
                ) : (
                  <>
                    Request a Strategy Call <ArrowUpRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value, href }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper href={href} className="flex items-center gap-4 p-4 gt-hairline rounded-xl bg-white/[0.02] gt-hover-lift block">
      <div className="w-10 h-10 rounded-lg bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center">
        <Icon className="w-4 h-4 text-emerald-400" />
      </div>
      <div>
        <div className="text-[11px] tracking-[0.3em] text-neutral-500 uppercase">{label}</div>
        <div className="text-white mt-1">{value}</div>
      </div>
    </Wrapper>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-xs tracking-[0.3em] text-neutral-500 uppercase">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full bg-transparent gt-hairline rounded-xl px-4 py-3 text-white placeholder:text-neutral-600 focus:border-emerald-400 outline-none"
      />
    </div>
  );
}
